import axios from 'axios';
import useAuthStore from 'Store/useAuthStore';
import useEntitlementsStore from 'Store/useEntitlementsStore';
import { API_ENDPOINTS } from 'config/ApiConfig';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USERNAME_KEY,
  NAME_KEY,
  DOMAIN_KEY,
  SUBSCRIPTION_TYPE_KEY,
  AUTH_TYPE_KEY,
  SSO_IDP_LOGIN_URL_KEY,
} from 'constants/localStorage.constants';

export const clearAuthStorage = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USERNAME_KEY);
  localStorage.removeItem(NAME_KEY);
  localStorage.removeItem(DOMAIN_KEY);
  localStorage.removeItem(SUBSCRIPTION_TYPE_KEY);
  localStorage.removeItem(AUTH_TYPE_KEY);
  localStorage.removeItem(SSO_IDP_LOGIN_URL_KEY);
};

export const clearAuthStores = () => {
  useAuthStore.getState().clearAuth();
  useEntitlementsStore.getState().clearEntitlements();
};

export const clearClientAuthState = () => {
  clearAuthStorage();
  clearAuthStores();
};

export const redirectToLogin = () => {
  if (typeof window === 'undefined') return;
  window.location.assign('/');
};

// Best-effort server-side revoke on a forced (401) logout. Uses raw axios
// (not the shared `Api` instance, and not apiService's logoutUser) to avoid a
// circular import — axiosInstance -> authSession -> apiService -> axiosInstance
// — and to keep this independent of the interceptor that triggers this flow.
// The access token may already be expired/invalid, so failures are ignored —
// this must never block clearing local state and redirecting the user.
//
// Note: this intentionally duplicates the request shape of apiService's
// logoutUser() rather than calling it, for the circular-import reason above.
// Keep the header name in sync with that function if it ever changes.
const attemptBackendLogout = (accessToken, refreshToken) => {
  if (!accessToken) return;
  axios.delete(API_ENDPOINTS.LOGOUT, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Refresh-Token': refreshToken,
      'Content-Type': 'application/json',
    },
  }).catch(() => {
    // Ignore — token is likely already invalid/expired.
  });
};

// Several requests can fail with an invalid session at once (e.g. a page
// firing multiple calls right as the token expires) — this guard collapses
// them to a single backend-logout attempt and redirect instead of one per
// failed request. Never reset: redirectToLogin() reloads the page, which
// clears this along with all other module state.
let isHandlingUnauthorizedSession = false;

export const handleUnauthorizedSession = () => {
  if (isHandlingUnauthorizedSession) return;
  isHandlingUnauthorizedSession = true;

  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

  attemptBackendLogout(accessToken, refreshToken);
  clearClientAuthState();
  redirectToLogin();
};

// flask-jwt-extended returns 422 (not 401) when the JWT itself fails to decode
// or validate — e.g. a garbage/malformed access token never reaches the
// "is it expired" check, it fails earlier and comes back as 422. That must
// still force a logout. But 422 is also the generic status many of our own
// endpoints use for normal payload validation errors, so we only treat a 422
// as an auth error when its message matches a known flask-jwt-extended
// decode/validation failure (from response.data.msg) — never on status alone.
const JWT_AUTH_ERROR_MESSAGE_PATTERNS = [
  /not enough segments/i,
  /invalid header/i,
  /invalid token/i,
  /malformed token/i,
  /wrong token type/i,
  /only \w+ tokens? are allowed/i,
  /signature verification failed/i,
  /invalid crypto padding/i,
  /invalid payload padding/i,
  /token has expired/i,
  /token has been revoked/i,
  /fresh token required/i,
  /subject must be a string/i,
];

export const isJwtAuthError = (error) => {
  const response = error?.response;
  if (response?.status !== 422) return false;

  const msg = response?.data?.msg;
  if (typeof msg !== 'string') return false;

  const isMatch = JWT_AUTH_ERROR_MESSAGE_PATTERNS.some((pattern) => pattern.test(msg));

  // If the backend's wording ever drifts from these patterns, a real JWT
  // error would silently stop forcing a logout. Surface that in the console
  // instead of failing silently, without changing the (conservative) result.
  if (!isMatch) {
    console.debug('[authSession] 422 response did not match any known JWT error pattern:', msg);
  }

  return isMatch;
};
